// The app's singletons. Everything testable takes these as arguments; only this file touches the real window.
import { QueryClient } from "@tanstack/react-query";
import { createApiClient, shouldRetry } from "./api";
import { createHostBridge, type BridgeWindow } from "./host-bridge";
import { createSessionStore } from "./session";
import { browserPushEnv, deviceTokenFor, forgetDeviceToken } from "./web-push";

export const bridge = createHostBridge(window as unknown as BridgeWindow);

export const api = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? "",
  getToken: () => sessionStore.getToken(),
  refreshSession: () => sessionStore.refreshAfterUnauthorized(),
});

/** The public site with the legal pages. Production sets VITE_SITE_URL; the default is the one the site itself uses. */
export const siteUrl = (): string => ((import.meta.env.VITE_SITE_URL as string | undefined) || "https://imagora.media").replace(/\/+$/, "");

/** Where the API answers, as a full address someone can paste into another tool. */
export const apiBaseUrl = (): string => (import.meta.env.VITE_API_URL as string | undefined) || window.location.origin;

/** Browser storage can throw, in a private window or with site data blocked. Then a reload asks for a new link. */
function tabStorage(key: string) {
  return {
    load(): string | null {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    save(value: string | null) {
      try {
        if (value) window.sessionStorage.setItem(key, value);
        else window.sessionStorage.removeItem(key);
      } catch {
        // Stays signed in until the next reload.
      }
    },
  };
}

/** The browser's push, for the notifications screen and for a tapped notice. */
export const pushEnv = browserPushEnv(bridge.embedded);

export const sessionStore = createSessionStore({
  exchange: (token) => api.exchange(token),
  bridge,
  direct: { ...tabStorage("imagora.direct-session"), restore: (token) => api.account.restore(token) },
  reopen: (token, tenantId) => api.account.switchTenantWith(token, tenantId),
  device: {
    load: (tenantId) => deviceTokenFor(pushEnv, tenantId),
    forget: (token) => forgetDeviceToken(pushEnv, token),
    open: (token) => api.account.deviceSession(token),
  },
});

/** The platform role's token, kept apart from any tenant session so neither can be sent by mistake in the other's place. */
export const platformToken = tabStorage("imagora.platform-session");

/** The operator's client: the same calls and errors, its own token, no refresh. */
export const platformApi = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? "",
  getToken: () => platformToken.load(),
  refreshSession: () => Promise.resolve(false),
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: shouldRetry, staleTime: 30_000, refetchOnWindowFocus: false },
    mutations: { retry: false },
  },
});
